import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Eye, Database, Shield, Cookie, Users, Mail } from 'lucide-react';
import { API } from '../config/api';



export default function PrivacyPage() {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    fetchPlatformConfig();
  }, []);

  const fetchPlatformConfig = async () => {
    try {
      const response = await fetch(`${API}/platform/config`);
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Failed to fetch platform config:', error);
    }
  };

  const contactEmail = config?.support_contact || 'contact@dmvsoundstage.com';

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="privacy-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/20 text-primary">Legal</Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase mb-4">
            Privacy <span className="text-secondary">Policy</span>
          </h1>
          <p className="text-muted-foreground">Last updated: January 2025</p>
        </div>

        <div className="space-y-8">
          {/* Introduction */}
          <Card className="glass-card border-border p-6">
            <h2 className="font-heading text-xl font-bold uppercase mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-primary" />
              Overview
            </h2>
            <p className="text-muted-foreground">
              DMV Soundstage ("we", "us", "our") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your 
              information when you use our platform.
            </p>
          </Card>

          {/* Information We Collect */}
          <Card className="glass-card border-border p-6">
            <h2 className="font-heading text-xl font-bold uppercase mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-secondary" />
              Information We Collect
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <div>
                <h3 className="font-medium text-foreground mb-2">Personal Information</h3>
                <ul className="space-y-1 text-sm">
                  <li>• Email address and name (required for account creation)</li>
                  <li>• Profile information (stage name, bio, social links)</li>
                  <li>• Payment information (processed securely via Stripe)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-2">Content Data</h3>
                <ul className="space-y-1 text-sm">
                  <li>• Music, videos, and podcasts you upload</li>
                  <li>• Comments and interactions</li>
                  <li>• Playlist and request history</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-2">Usage Data</h3>
                <ul className="space-y-1 text-sm">
                  <li>• Device and browser information</li>
                  <li>• IP address (hashed for privacy)</li>
                  <li>• Pages visited and features used</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* How We Use Information */}
          <Card className="glass-card border-border p-6">
            <h2 className="font-heading text-xl font-bold uppercase mb-4">How We Use Your Information</h2>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                To provide and maintain our platform services
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                To process subscriptions and payments
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                To calculate charts and rankings based on requests
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                To moderate content and enforce community guidelines
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                To communicate with you about your account
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                To improve our platform and develop new features
              </li>
            </ul>
          </Card>

          {/* Data Security */}
          <Card className="glass-card border-secondary/30 p-6">
            <h2 className="font-heading text-xl font-bold uppercase mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-secondary" />
              Data Security
            </h2>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-secondary mt-1">•</span>
                Passwords are encrypted using industry-standard hashing
              </li>
              <li className="flex items-start gap-2">
                <span className="text-secondary mt-1">•</span>
                Payment processing is handled securely by Stripe
              </li>
              <li className="flex items-start gap-2">
                <span className="text-secondary mt-1">•</span>
                We use HTTPS encryption for all data transmission
              </li>
              <li className="flex items-start gap-2">
                <span className="text-secondary mt-1">•</span>
                IP addresses are hashed before storage
              </li>
            </ul>
          </Card>

          {/* Cookies */}
          <Card className="glass-card border-border p-6">
            <h2 className="font-heading text-xl font-bold uppercase mb-4 flex items-center gap-2">
              <Cookie className="w-5 h-5 text-primary" />
              Cookies & Tracking
            </h2>
            <p className="text-muted-foreground mb-4">
              We use essential cookies to:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                Keep you logged in to your account
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                Remember your preferences
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                Prevent fraudulent activity
              </li>
            </ul>
          </Card>

          {/* Third Parties */}
          <Card className="glass-card border-border p-6">
            <h2 className="font-heading text-xl font-bold uppercase mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Third-Party Services
            </h2>
            <p className="text-muted-foreground mb-4">
              We work with trusted third parties to provide our services:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <strong>Stripe:</strong> Payment processing (see Stripe's Privacy Policy)
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <strong>Google:</strong> OAuth authentication (if you sign in with Google)
              </li>
            </ul>
          </Card>

          {/* Your Rights */}
          <Card className="glass-card border-border p-6">
            <h2 className="font-heading text-xl font-bold uppercase mb-4">Your Rights</h2>
            <p className="text-muted-foreground mb-4">You have the right to:</p>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-secondary mt-1">•</span>
                Access the personal data we hold about you
              </li>
              <li className="flex items-start gap-2">
                <span className="text-secondary mt-1">•</span>
                Request correction of inaccurate data
              </li>
              <li className="flex items-start gap-2">
                <span className="text-secondary mt-1">•</span>
                Request deletion of your account and data
              </li>
              <li className="flex items-start gap-2">
                <span className="text-secondary mt-1">•</span>
                Export your content and data
              </li>
            </ul>
          </Card>

          {/* Contact */}
          <Card className="glass-card border-primary/30 p-6">
            <h2 className="font-heading text-xl font-bold uppercase mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Contact Us
            </h2>
            <p className="text-muted-foreground mb-4">
              For privacy-related inquiries or to exercise your rights, please contact:
            </p>
            <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Privacy & Support:</p>
              <a href={`mailto:${contactEmail}`} className="text-primary hover:text-primary/80 font-mono text-lg">
                {contactEmail}
              </a>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
